import {
	BN,
	MarketType,
	L2OrderBook,
	BigNum,
	PositionDirection,
	ZERO,
	PRICE_PRECISION_EXP,
	BASE_PRECISION,
	BASE_PRECISION_EXP,
	calculateEstimatedEntryPriceWithL2,
} from '@drift-labs/sdk';
import { MarketId, ENUM_UTILS, UIMarket } from '@drift/common';
import Env from '../environmentVariables/EnvironmentVariables';

// Helper function to calculate showPriceEstimateOracleDivergenceWarning
export const calculateShowPriceEstimateOracleDivergenceWarning = (
	entryPrice: BN,
	oraclePrice: BN,
	side: 'buy' | 'sell',
	orderType: 'market' | 'oracle' | 'limit',
	hasBaseSize: boolean
): boolean => {
	// Only show warning for market or oracle orders with base size
	if ((orderType !== 'market' && orderType !== 'oracle') || !hasBaseSize) {
		return false;
	}

	// Calculate threshold percentage based on entry price
	const thresholdPct = BigNum.from(entryPrice, PRICE_PRECISION_EXP).scale(
		Env.priceDivergenceWarningThreshold,
		100
	);

	// Check if divergence exceeds threshold
	const divergence = BigNum.from(
		entryPrice.sub(oraclePrice),
		PRICE_PRECISION_EXP
	).abs();

	const exceedsThreshold = divergence.gt(thresholdPct);

	// Check if divergence direction matches trade side
	const correctDirection =
		(side === 'buy' && entryPrice.gt(oraclePrice)) ||
		(side === 'sell' && entryPrice.lt(oraclePrice));

	return exceedsThreshold && correctDirection;
};

export type PriceImpactInfo = {
	marketIndex: number;
	marketType: MarketType;
	entryPrice: BN;
	priceImpact: BN;
	baseAvailable: BN;
	bestPrice: BN;
	worstPrice: BN;
	priceImpactInputBaseSize: BN;
	showPriceEstimateOracleDivergenceWarning: boolean;
	exceedsLiquidity: boolean;
};

// Helper function to calculate price impact from L2 data
export const calculatePriceImpactFromL2 = (
	marketId: MarketId,
	direction: PositionDirection,
	baseAmount: BN,
	l2Data: L2OrderBook,
	_oraclePrice?: BN
): {
	entryPrice: BN;
	priceImpact: BN;
	baseAvailable: BN;
	bestPrice: BN;
	worstPrice: BN;
	exceedsLiquidity: boolean;
	showPriceEstimateOracleDivergenceWarning: boolean;
} => {
	let [entryPrice, priceImpact, baseFilled, bestPrice, worstPrice] = [
		ZERO,
		ZERO,
		ZERO,
		ZERO,
		ZERO,
	];
	let exceedsLiquidity = false;

	try {
		if (marketId.isPerp) {
			const entryResult = calculateEstimatedEntryPriceWithL2(
				'base', // leadSide
				baseAmount,
				direction,
				BASE_PRECISION,
				l2Data
			);

			entryPrice = entryResult?.entryPrice || ZERO;
			priceImpact = entryResult?.priceImpact || ZERO;
			baseFilled = entryResult?.baseFilled || ZERO;
			bestPrice = entryResult?.bestPrice || ZERO;
			worstPrice = entryResult?.worstPrice || ZERO;
		} else {
			const precisionExp =
				UIMarket.spotMarkets[marketId.marketIndex]?.precisionExp ||
				BASE_PRECISION_EXP;

			const entryResult = calculateEstimatedEntryPriceWithL2(
				'base', // leadSide
				baseAmount,
				direction,
				precisionExp,
				l2Data
			);

			entryPrice = entryResult?.entryPrice || ZERO;
			priceImpact = entryResult?.priceImpact || ZERO;
			baseFilled = entryResult?.baseFilled || ZERO;
			bestPrice = entryResult?.bestPrice || ZERO;
			worstPrice = entryResult?.worstPrice || ZERO;
		}

		// Check if we exceeded available liquidity
		if (baseFilled.lt(baseAmount)) {
			exceedsLiquidity = true;
		}
	} catch (e) {
		// SDK function may throw error if no liquidity, use fallback values
		console.warn('Error calculating price impact from L2:', e);

		// Use best bid/ask as fallback
		const bestBid = l2Data.bids?.[0];
		const bestAsk = l2Data.asks?.[0];

		if (bestBid && bestAsk) {
			const isLong = ENUM_UTILS.match(direction, PositionDirection.LONG);
			entryPrice = isLong ? bestAsk.price : bestBid.price;
			bestPrice = isLong ? bestAsk.price : bestBid.price;
			worstPrice = isLong ? bestAsk.price : bestBid.price;
			baseFilled = isLong ? bestAsk.size : bestBid.size;
		}
	}

	return {
		entryPrice,
		priceImpact,
		baseAvailable: baseFilled,
		bestPrice,
		worstPrice,
		exceedsLiquidity,
		showPriceEstimateOracleDivergenceWarning: false, // Will be calculated by the caller
	};
};
